import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Navbar from './components/Navbar';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import AdminPage from './pages/AdminPage';
import Footer from './components/Footer';
import { ProductProvider } from './context/ProductContext';
import ChatWidget from './components/ChatWidget';
import AdminChat from './components/AdminChat';
import './index.css';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000, // 5 minutes
        },
    },
});

// ✅ ENHANCED THEME CONFIGURATION
const theme = createTheme({
    palette: {
        primary: {
            main: '#667eea',
            light: '#7b93f0',
            dark: '#4a5fc7',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#764ba2',
            light: '#8b5fbf',
            dark: '#5a3a7a',
            contrastText: '#ffffff',
        },
        success: {
            main: '#4CAF50',
            light: '#81C784',
            dark: '#388E3C',
        },
        warning: {
            main: '#FF9800',
            light: '#FFB74D',
            dark: '#F57C00',
        },
        error: {
            main: '#f44336',
            light: '#e57373',
            dark: '#d32f2f',
        },
        info: {
            main: '#2196F3',
            light: '#64B5F6',
            dark: '#1976D2',
        },
        background: {
            default: '#f8f9fa',
            paper: '#ffffff',
        },
        text: {
            primary: '#2d3436',
            secondary: '#636e72',
            disabled: '#b2bec3',
        },
        grey: {
            50: '#fafafa',
            100: '#f5f5f5',
            200: '#eeeeee',
            300: '#e0e0e0',
            400: '#bdbdbd',
            500: '#9e9e9e',
            600: '#757575',
            700: '#616161',
            800: '#424242',
            900: '#212121',
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
            fontWeight: 800,
            fontSize: '3.5rem',
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
        },
        h2: {
            fontWeight: 700,
            fontSize: '2.75rem',
            lineHeight: 1.25,
            letterSpacing: '-0.01em',
        },
        h3: {
            fontWeight: 700,
            fontSize: '2.25rem',
            lineHeight: 1.3,
        },
        h4: {
            fontWeight: 600,
            fontSize: '1.75rem',
            lineHeight: 1.35,
        },
        h5: {
            fontWeight: 600,
            fontSize: '1.5rem',
            lineHeight: 1.4,
        },
        h6: {
            fontWeight: 600,
            fontSize: '1.25rem',
            lineHeight: 1.4,
        },
        subtitle1: {
            fontSize: '1rem',
            fontWeight: 500,
            lineHeight: 1.5,
        },
        subtitle2: {
            fontSize: '0.875rem',
            fontWeight: 500,
            lineHeight: 1.5,
        },
        body1: {
            fontSize: '1rem',
            lineHeight: 1.6,
        },
        body2: {
            fontSize: '0.875rem',
            lineHeight: 1.6,
        },
        button: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
        },
        caption: {
            fontSize: '0.75rem',
            lineHeight: 1.5,
        },
        overline: {
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
        },
    },
    shape: {
        borderRadius: 12,
    },
    shadows: [
        'none',
        '0 1px 3px rgba(0,0,0,0.06)',
        '0 2px 6px rgba(0,0,0,0.08)',
        '0 4px 12px rgba(0,0,0,0.08)',
        '0 6px 20px rgba(0,0,0,0.08)',
        '0 8px 30px rgba(0,0,0,0.1)',
        '0 10px 40px rgba(0,0,0,0.1)',
        '0 12px 50px rgba(0,0,0,0.1)',
        '0 14px 60px rgba(0,0,0,0.12)',
        '0 16px 70px rgba(0,0,0,0.12)',
        '0 18px 80px rgba(0,0,0,0.12)',
        '0 20px 90px rgba(0,0,0,0.14)',
        '0 22px 100px rgba(0,0,0,0.14)',
        '0 24px 110px rgba(0,0,0,0.14)',
        '0 26px 120px rgba(0,0,0,0.16)',
        '0 28px 130px rgba(0,0,0,0.16)',
        '0 30px 140px rgba(0,0,0,0.16)',
        '0 32px 150px rgba(0,0,0,0.18)',
        '0 34px 160px rgba(0,0,0,0.18)',
        '0 36px 170px rgba(0,0,0,0.18)',
        '0 38px 180px rgba(0,0,0,0.2)',
        '0 40px 190px rgba(0,0,0,0.2)',
        '0 42px 200px rgba(0,0,0,0.2)',
        '0 44px 210px rgba(0,0,0,0.22)',
        '0 46px 220px rgba(0,0,0,0.22)',
    ],
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    boxShadow: 'none',
                    padding: '8px 24px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        boxShadow: '0 4px 12px rgba(102,126,234,0.3)',
                        transform: 'translateY(-2px)',
                    },
                    '&:active': {
                        transform: 'translateY(0)',
                    },
                },
                contained: {
                    '&:hover': {
                        boxShadow: '0 6px 20px rgba(102,126,234,0.35)',
                    },
                },
                containedPrimary: {
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4292 100%)',
                    },
                },
                outlined: {
                    borderWidth: 2,
                    '&:hover': {
                        borderWidth: 2,
                    },
                },
                sizeSmall: {
                    padding: '4px 16px',
                    fontSize: '0.8125rem',
                },
                sizeLarge: {
                    padding: '12px 32px',
                    fontSize: '1rem',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                        transform: 'translateY(-4px)',
                    },
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: 16,
                    '&:last-child': {
                        paddingBottom: 16,
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    height: 28,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        transform: 'translateY(-1px)',
                    },
                },
                colorPrimary: {
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                },
                outlined: {
                    borderWidth: 2,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                },
                elevation1: {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                },
                elevation2: {
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                },
                elevation3: {
                    boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                },
                colorPrimary: {
                    backgroundColor: '#1a1a2e',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 8,
                        '&:hover fieldset': {
                            borderColor: '#667eea',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#667eea',
                            borderWidth: 2,
                        },
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 16,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#1a1a2e',
                },
            },
        },
        MuiBadge: {
            styleOverrides: {
                badge: {
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    minWidth: 18,
                    height: 18,
                    borderRadius: 9,
                },
            },
        },
        MuiRating: {
            styleOverrides: {
                root: {
                    color: '#FFB800',
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                },
                standardError: {
                    backgroundColor: '#fde8e8',
                    color: '#c62828',
                },
                standardSuccess: {
                    backgroundColor: '#e8f5e9',
                    color: '#2e7d32',
                },
                standardWarning: {
                    backgroundColor: '#fff3e0',
                    color: '#e65100',
                },
                standardInfo: {
                    backgroundColor: '#e3f2fd',
                    color: '#0d47a1',
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: 'rgba(0,0,0,0.06)',
                },
            },
        },
        MuiContainer: {
            styleOverrides: {
                root: {
                    paddingLeft: 16,
                    paddingRight: 16,
                    '@media (min-width: 600px)': {
                        paddingLeft: 24,
                        paddingRight: 24,
                    },
                },
                maxWidthLg: {
                    '@media (min-width: 1280px)': {
                        maxWidth: 1200,
                    },
                },
            },
        },
        MuiGrid: {
            styleOverrides: {
                root: {
                    marginTop: 0,
                },
            },
        },
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
        },
    },
    transitions: {
        duration: {
            shortest: 150,
            shorter: 200,
            short: 250,
            standard: 300,
            complex: 375,
            enteringScreen: 225,
            leavingScreen: 195,
        },
        easing: {
            easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
            easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
            easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
            sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
        },
    },
});

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <ProductProvider>
                    <Router>
                        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                            <Navbar />
                            <main style={{ flex: 1 }}>
                                <Routes>
                                    <Route path="/" element={<HomePage />} />
                                    <Route path="/products" element={<ProductsPage />} />
                                    <Route path="/products/:id" element={<ProductDetailPage />} />
                                    <Route path="/product/:slug" element={<ProductDetailPage />} />
                                    <Route path="/product/id/:id" element={<ProductDetailPage />} />
                                    <Route path="/cart" element={<CartPage />} />
                                    <Route path="/admin" element={<AdminPage />} />
                                    <Route path="/admin/chat" element={<AdminChat />} />
                                </Routes>
                            </main>
                            <Footer />
                            <ChatWidget />
                        </div>
                    </Router>
                </ProductProvider>
            </ThemeProvider>
        </QueryClientProvider>
    );
}

export default App;